import axios from "axios";
import { User, AuthResponse } from "../types";
import { supabase } from "./supabase";
import { useAuthStore } from "../stores/authStore";

const DEFAULT_API_BASE_URL = import.meta.env.VITE_API_URL || "/api";

// Função para buscar todas as stores disponíveis (excluindo multistore)
export async function fetchAvailableStores(): Promise<
  { storename: string; api_base_url: string; preview_base_url: string }[]
> {
  try {
    // Em desenvolvimento, usar endpoint local. Em produção, endpoint do 'multistore' para buscar a lista.
    const apiUrl = import.meta.env.DEV
      ? "/api"
      : "https://api-editor.wicomm.com.br/api";

    const response = await fetch(`${apiUrl}/stores`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Erro ao buscar stores");
    }

    const data = await response.json();
    return data.stores || [];
  } catch (error) {
    console.error("Erro ao buscar stores:", error);
    return [];
  }
}

function resolveApiBaseUrl(): string {
  // Para desenvolvimento local, sempre usar o proxy do Vite
  if (import.meta.env.DEV) {
    return "/api";
  }

  try {
    // Em produção, a fonte da verdade é o 'site-config' salvo no localStorage
    // após o login ou seleção de loja.
    const siteCfgRaw = localStorage.getItem("site-config");
    if (siteCfgRaw) {
      const siteCfg = JSON.parse(siteCfgRaw);
      if (siteCfg.apiBaseUrl) {
        return siteCfg.apiBaseUrl as string;
      }
    }
  } catch (error) {
    console.error(
      "Falha ao resolver a URL base da API a partir do site-config:",
      error,
    );
  }

  // Fallback para o padrão, embora o fluxo normal deva sempre ter o site-config.
  return DEFAULT_API_BASE_URL;
}

// Configuração do axios
const api = axios.create({
  baseURL: resolveApiBaseUrl(),
  headers: {
    "Content-Type": "application/json",
  },
  // Sem timeout o axios pode ficar pendurado para sempre (ex: backend reiniciando
  // por nodemon/pm2 com conexão TCP aberta). 30s cobre cold-start de storefront
  // e operações pesadas; depois disso o erro é propagado pro catch dos stores.
  timeout: 30000,
});

// Interceptor para atualizar baseURL dinamicamente se o storeName mudar
api.interceptors.request.use((config) => {
  const dynamicBase = resolveApiBaseUrl();
  if (config.baseURL !== dynamicBase) {
    config.baseURL = dynamicBase;
  }

  try {
    const token = localStorage.getItem("auth-storage");
    if (token) {
      const authData = JSON.parse(token);
      const user = authData.state?.user;
      const authToken = authData.state?.token;
      const useDevBranch = authData.state?.useDevBranch || false;

      // Adicionar token de autorização, se existir
      if (authToken) {
        config.headers.Authorization = `Bearer ${authToken}`;
      }

      // Para usuários multistore ou multi-loja (2+ lojas no perfil), o storeName
      // selecionado DEVE ser enviado via header para que o backend possa
      // direcionar a requisição para o tenant correto. Não enviar o valor CSV
      // cru (antes da seleção de loja) — o backend rejeitaria com 403.
      const hasChoice = user?.isMultiStore || (user?.allowedStores?.length || 0) > 1;
      if (hasChoice && user?.storeName && !user.storeName.includes(",")) {
        config.headers["x-store-name"] = user.storeName;
      }

      // Passar flag de dev mode via header para que o backend possa usar
      if (useDevBranch) {
        config.headers["x-use-dev-branch"] = "true";
      }
    }
  } catch (error) {
    console.error("Erro ao processar auth-storage no interceptor:", error);
  }

  return config;
});

// Interceptor para tratamento de erros
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || "";
      // Evita redirecionar durante a tentativa de login
      if (!requestUrl.endsWith("/auth/login")) {
        localStorage.removeItem("auth-storage");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  },
);

// Adiciona o token de autorização, se disponível
api.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore.getState();
    const token = authStore.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Intercepta respostas para lidar com erros de autenticação
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      useAuthStore.getState().logout();
      window.location.href = "/";
    }
    return Promise.reject(error);
  },
);

// API de autenticação
export const authApi = {
  setup: async (
    useDevBranch: boolean = false,
  ): Promise<{ success: boolean; message: string; clientSite: any }> => {
    // Garantir que o token seja enviado manualmente se necessário
    const token = localStorage.getItem("auth-storage");
    let authToken = "";
    if (token) {
      try {
        const authData = JSON.parse(token);
        authToken = authData.state?.token || "";
      } catch (error) {
        // console.error('Erro ao parsear token para setup:', error)
      }
    }

    const response = await api.post(
      "/auth/setup",
      { useDevBranch },
      {
        headers: {
          ...(authToken && { Authorization: `Bearer ${authToken}` }),
        },
      },
    );
    return response.data;
  },
};

// Tipos do editor (mantidos como any por enquanto)
export type WakeSiteConfig = any;
export type LayoutConfig = any;

// API de sites
export const sitesApi = {
  list: async (): Promise<{ sites: WakeSiteConfig[]; count: number }> => {
    const response = await api.get("/sites");
    return response.data;
  },

  create: async (
    domain: string,
    token: string,
  ): Promise<{ success: boolean; site: WakeSiteConfig }> => {
    const response = await api.post("/sites", { domain, token });
    return response.data;
  },

  update: async (
    siteId: string,
    data: Partial<WakeSiteConfig>,
  ): Promise<{ success: boolean }> => {
    const response = await api.put(`/sites/${siteId}`, data);
    return response.data;
  },

  updateEditingJson: async (jsonData: any): Promise<{ success: boolean }> => {
    const response = await api.post("/wake-json/update-editing-json", {
      jsonData,
    });
    return response.data;
  },

  delete: async (siteId: string): Promise<{ success: boolean }> => {
    const response = await api.delete(`/sites/${siteId}`);
    return response.data;
  },

  test: async (
    siteId: string,
  ): Promise<{ success: boolean; status: string }> => {
    const response = await api.post(`/sites/${siteId}/test`);
    return response.data;
  },

  resolve: async (): Promise<{
    storeName: string;
    apiBaseUrl: string;
    previewBaseUrl: string;
  }> => {
    // Garantir que o token seja enviado manualmente se necessário
    const token = localStorage.getItem("auth-storage");
    let authToken = "";
    if (token) {
      try {
        const authData = JSON.parse(token);
        authToken = authData.state?.token || "";
      } catch (error) {
        // console.error('Erro ao parsear token para resolve:', error)
      }
    }

    const response = await api.get("/sites/resolve", {
      headers: {
        ...(authToken && { Authorization: `Bearer ${authToken}` }),
      },
    });
    return response.data;
  },
};

// API de renderização
export const renderApi = {
  apply: async (): Promise<{
    success: boolean;
    message: string;
    timestamp?: string;
  }> => {
    const response = await api.post("/render/apply");
    return response.data;
  },

  reset: async (): Promise<{ success: boolean; message: string }> => {
    const response = await api.post("/render/reset");
    return response.data;
  },

  getComponents: async (page: string = 'home'): Promise<{ success: boolean; components: any[] }> => {
    const response = await api.get(`/render/components?page=${page}`);
    return response.data;
  },

  getRegistry: async (): Promise<{ registry: any[] }> => {
    const response = await api.get("/render/registry");
    return response.data;
  },


  getDevLayout: async (): Promise<{ success: boolean; layout: any; components_registry?: any[] }> => {
    const response = await api.get("/render/dev/layout");
    return response.data;
  },

  getDevSettings: async (): Promise<{ success: boolean; layout: any; components_registry?: any[] }> => {
    const response = await api.get("/render/dev/settings");
    return response.data;
  },

  getWakeJson: async (): Promise<{ success: boolean; layout: any; components_registry?: any[] }> => {
    const response = await api.get("/render/dev/wake-json");
    return response.data;
  },

  saveDevLayout: async (
    layout: any,
    components_registry?: any[],
  ): Promise<{ success: boolean; message: string; timestamp?: string }> => {
    const response = await api.post("/render/dev/layout", { layout, components_registry });
    return response.data;
  },

  publishDev: async (): Promise<{
    success: boolean;
    message: string;
    version?: any;
  }> => {
    const response = await api.post("/render/dev/publish");
    return response.data;
  },

  syncDevSettings: async (): Promise<{
    success: boolean;
    message: string;
    layout: any;
    components_registry?: any[];
  }> => {
    const response = await api.post("/render/dev/sync-settings");
    return response.data;
  },

  updateEditingJson: async (data: {
    components: any[];
    page?: string;
  }): Promise<{ success: boolean; message: string }> => {
    const response = await api.post("/render/update-json", data);
    return response.data;
  },

  load: async (
    siteId: string,
    page: string,
  ): Promise<{ layoutConfig: LayoutConfig; storefrontPort?: number }> => {
    const response = await api.get(`/render/load/${siteId}/${encodeURIComponent(page)}`);
    return response.data;
  },

  publish: async (): Promise<{
    success: boolean;
    message: string;
    version?: any;
  }> => {
    const response = await api.post("/render/publish");
    return response.data;
  },

  listVersions: async (
    limit: number = 10,
  ): Promise<{ success: boolean; versions: any[]; total: number }> => {
    const response = await api.get(`/render/versions?limit=${limit}`);
    return response.data;
  },

  applyVersion: async (
    versionId: string,
  ): Promise<{ success: boolean; message: string; version: string }> => {
    const response = await api.post(`/render/versions/${versionId}/apply`);
    return response.data;
  },

  // Scheduling endpoints
  schedulePublication: async (
    scheduledAt: string,
  ): Promise<{
    success: boolean;
    message: string;
    scheduledPublication: any;
  }> => {
    const response = await api.post("/render/schedule", { scheduledAt });
    return response.data;
  },

  listScheduledPublications: async (): Promise<{
    success: boolean;
    scheduledPublications: any[];
    total: number;
  }> => {
    const response = await api.get("/render/scheduled");
    return response.data;
  },

  cancelScheduledPublication: async (
    publicationId: string,
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/render/scheduled/${publicationId}`);
    return response.data;
  },
};

// API de upload
export const uploadApi = {
  uploadImage: async (
    file: File,
    type: "desktop" | "mobile",
    storeName: string,
    options?: { width?: number; height?: number; quality?: number },
  ): Promise<{
    success: boolean;
    url: string;
    filename: string;
    size: number;
    mimetype: string;
  }> => {
    const formData = new FormData();
    formData.append("image", file);
    formData.append("type", type);
    formData.append("storeName", storeName); // Enviar o storeName ativo

    if (options?.width) formData.append("maxWidth", options.width.toString());
    if (options?.height)
      formData.append("maxHeight", options.height.toString());
    if (options?.quality)
      formData.append("quality", options.quality.toString());

    const response = await api.post("/upload/image", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  uploadVideo: async (
    file: File,
    type: "desktop" | "mobile",
    storeName: string,
    options?: {
      quality?: number;
      maxDuration?: number;
      onProgress?: (percent: number) => void;
    },
  ): Promise<{
    success: boolean;
    url: string;
    filename: string;
    size: number;
    originalSize?: number;
    mimetype: string;
    thumbnailUrl?: string;
  }> => {
    const formData = new FormData();
    formData.append("video", file);
    formData.append("type", type);
    formData.append("storeName", storeName); // Enviar o storeName ativo

    if (options?.quality)
      formData.append("quality", options.quality.toString());
    if (options?.maxDuration)
      formData.append("maxDuration", options.maxDuration.toString());

    const response = await api.post("/upload/video", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      // O backend recomprime o vídeo com ffmpeg antes de responder, o que passa
      // folgado dos 30s padrão do cliente
      timeout: 5 * 60 * 1000,
      onUploadProgress: (event) => {
        if (!options?.onProgress || !event.total) return;
        options.onProgress(Math.round((event.loaded * 100) / event.total));
      },
    });
    return response.data;
  },

  listImages: async (
    storeName: string,
  ): Promise<{
    images: Array<{
      filename: string;
      url: string;
      uploadedAt: Date;
    }>;
  }> => {
    const response = await api.get(`/upload/images?storeName=${storeName}`);
    return response.data;
  },

  listMedia: async (
    storeName: string,
    type: "desktop" | "mobile" = "desktop",
    page: number = 1,
    limit: number = 32,
    mediaType: "image" | "video" | "all" = "all",
  ): Promise<{
    media: Array<{
      filename: string;
      url: string;
      uploadedAt: Date;
      type: "image" | "video";
      thumbnailUrl?: string; // Unificado
    }>;
    pagination: {
      page: number;
      limit: number;
      hasMore: boolean;
    };
  }> => {
    const response = await api.get(
      `/upload/media?storeName=${storeName}&type=${type}&page=${page}&limit=${limit}&mediaType=${mediaType}`,
    );
    return response.data;
  },

  checkImageUsage: async (
    filename: string,
    storeName: string,
  ): Promise<{
    isUsed: boolean;
    components: string[];
    locations: string[];
    message: string;
  }> => {
    const response = await api.get(
      `/upload/image/${filename}/usage?storeName=${storeName}`,
    );
    return response.data;
  },

  checkMediaUsage: async (
    filename: string,
    storeName: string,
  ): Promise<{
    isUsed: boolean;
    components: string[];
    locations: string[];
    message: string;
  }> => {
    const response = await api.get(
      `/upload/media/${filename}/usage?storeName=${storeName}`,
    );
    return response.data;
  },

  deleteImage: async (
    filename: string,
    storeName: string,
  ): Promise<{ success: boolean }> => {
    const response = await api.delete(
      `/upload/image/${filename}?storeName=${storeName}`,
    );
    return response.data;
  },

  deleteMedia: async (
    filename: string,
    storeName: string,
  ): Promise<{ success: boolean }> => {
    const response = await api.delete(
      `/upload/media/${filename}?storeName=${storeName}`,
    );
    return response.data;
  },
};

export const statusApi = {
  getOnlineUsers: async (): Promise<{ onlineUsers: any[] }> => {
    const response = await api.get("/status");
    return response.data;
  }
};

export default api;
